<template>
  <div class="articles-container">
    <div id="heading1" class="article-section">
      <h2 class="h3">
        Confidence Interval Calculator
      </h2>
      <p class="bottom-margins">
        A <a href="https://en.wikipedia.org/wiki/Confidence_interval" target="_blank">
          confidence interval
        </a> is the range within which you expect the population parameter to be. Its estimation is based on the data you provide in the sample.
      </p>
      <p class="bottom-margins">
        The Confidence Interval Calculator allows you to compute the <strong>confidence interval of a mean by providing</strong> the standard deviation, mean, and sample size. Alternatively, if you don’t know the aforementioned measures, the tool will estimate them for you. In addition, you can use it with any arbitrary confidence level.
      </p>
      <p class="bottom-margins">
        There are two circumstances where we calculate the confidence intervals for a population—when the population variance is known and when it’s unknown. Depending on that, the calculator would use the <a href="https://365datascience.com/tutorials/statistics-tutorials/normal-distribution/" target="_blank">
          normal distribution
        </a> or the <a href="https://365datascience.com/tutorials/statistics-tutorials/students-t-distribution/" target="_blank">
          Student's t-distribution
        </a> for the confidence interval of the mean.
      </p>
      <p>The calculator provides the necessary formulas, as well as a step-by-step solution.</p>
    </div>

    <div id="heading2" class="article-section">
      <h3 class="h4">
        What Is a Confidence Interval?
      </h3>
      <p class="bottom-margins">
        A confidence interval is the range within which you expect the population parameter to be. When we use sample data, we aren’t certain of the exact value of the parameter we examine. Therefore, confidence intervals help us calculate whether the results we obtain will match those from a population. They are usually expressed at a designated confidence level — the degree of certainty that the estimated parameter’s true value will be in the confidence interval. The most widely used one is 95%, but others, such as 90% or 99%, are also common.
      </p>
      <p class="bottom-margins">
        For example, a 95% confidence interval would imply that we are 95% confident that the true population mean (µ) falls within this interval, with a 5% chance that it does not contain the true population mean (µ).
      </p>
      <p>
        We build the confidence interval around the <a href="https://365datascience.com/tutorials/statistics-tutorials/point-estimates-confidence-intervals/" target="_blank">
          point estimate
        </a> (the estimate of the population parameter). In fact, the point estimate is located exactly in the middle of the interval. However, the latter provides much more information and is the preferred choice for making inferences.
      </p>
      <img
        loading="lazy"
        alt="Confidence Interval. Interval start, end and the estimated point between them"
        class="img-interval"
        src="../../../../assets/images/calculators/confidence-interval/confidence-interval-01-desktop.png"
      >
    </div>

    <div id="heading3" class="article-section">
      <h3 class="h4">
        Which Confidence Level Should You Use?
      </h3>
      <p class="bottom-margins">
        The confidence level is equal to (1 – α). Alpha (α) denotes the level of significance or the probability of rejecting the null hypothesis when it‘s true—in other words, the probability of making a Type I Error.
      </p>
      <p class="bottom-margins">
        When constructing a confidence interval (or using a calculator), you pick the value of α. Put simply, you decide what risk you are willing to accept based on the certainty you need. Typical values are 0.01, 0.05, and 0.1.
      </p>
      <p>You want to be very precise, therefore, pick a low significance level such as 0.01. For example, to check whether a machine works properly, you expect the test to make little or no mistakes. In most cases, the context you operate in will determine α, but <strong>0.05</strong> is the most commonly used value.</p>
    </div>

    <div id="heading4" class="article-section">
      <h3 class="h4">
        Calculating the Confidence Interval (Formula)
      </h3>
      <p>The confidence interval’s lower bound equals the <strong>point estimate</strong>, minus the <strong>reliability factor</strong>, times the <strong>standard error</strong>. Meanwhile, the upper bound equals the <strong>point estimate</strong>, plus the <strong>reliability factor</strong>, times the <strong>standard error</strong>.</p>
      <p class="formulas-top-down svg">
        \[\bar{X}\pm\ Reliability\ Factor\ \times\ Standard\ Error\]
      </p>
      <p class="bottom-margins top-margins">
        There are two circumstances where we calculate the confidence intervals for a population—when the population variance is known and when it’s unknown. Depending on that, you or the Confidence Interval Calculator would use a different method to estimate.
      </p>
      <p>The confidence interval for a <strong>sample with known population variance</strong> is equal to the following:</p>
      <p class="formulas-top-down">
        \[\bar{X}\pm\ z_{\alpha/2}\frac{\sigma}{\sqrt n}\]
      </p>
      <p class="math-jax-display">
        The confidence interval’s lower bound equals the <strong>sample mean</strong> minus \[z_{\alpha/2}\], times the <strong>standard error</strong>. Meanwhile, the upper bound equals the <strong>sample mean</strong>, plus \[z_{\alpha/2}\], times the <strong>standard error</strong>.
      </p>
      <p class="formulas-top-down">
        \[\bar{X}\pm\ z_{\alpha/2}\frac{\sigma}{\sqrt n}\]
      </p>
      <p>Whereas, we calculate the confidence interval for a <strong>sample with unknown population variance</strong> as follows:</p>
      <p class="formulas-top-down">
        \[\bar{X}\pm\ t_{\left(n-1\right),\alpha/2}\frac{s}{\sqrt n}\]
      </p>
      <p class="bottom-margins">
        There are two key differences between the formulas for known and unknown variances:
      </p>
      <ol class="bottom-margins">
        <li>Instead of the z-score, we use the t-statistic.</li>
        <li>Instead of the population standard deviation, we use the sample standard deviation.</li>
      </ol>
      <p>The underlying logic in both cases is the same. The only two inputs that change are the standard deviation and the statistic at hand. When the population variance is <strong>known</strong>, we use the population standard deviation and the z-score. On the other hand, when the population variance is <strong>unknown</strong>, we use the sample standard deviation and the t-statistic.</p>
      <img
        class="two-images" loading="lazy" alt="Known and unknown standard deviations graph."
        src="../../../../assets/images/calculators/confidence-interval/confidence-interval-02-desktop.png"
      >
      <p class="math-jax-display">
        \[z_{\alpha/2}\] comes from the standard <nuxt-link to="/tables/standard-normal-distribution-table/" target="_blank">
          normal distribution table
        </nuxt-link>. We obtain it by finding the z-value that provides an area in the right tail of the z-distribution. Let’s say that we want the values for the 95% confidence interval. \[\alpha\] is 0.05, therefore, we are looking for \[\alpha/2\], or <strong>0.025</strong>, in the right tail of the z-distribution.
      </p>
      <img class="known-unknown" alt="Known standard deviations graph." src="../../../../assets/images/calculators/confidence-interval/confidence-interval-03-desktop.png">
      <p class="math-jax-display">
        In the table, this will match the value of 1– 0.025, or <strong>0.9750</strong>. The corresponding \[z\] comes from the sum of the row and column table headers associated with this cell.
      </p>
      <p class="tables-headings">
        Standard Normal Table
      </p>
      <img
        class="img-tables snd" loading="lazy" alt="Standard Normal Distribution Table."
        src="../../../../assets/images/calculators/confidence-interval/confidence-interval-04-desktop.webp"
      >
      <p class="bottom-margins math-jax-display">
        A commonly used term for is critical value. In our case, the critical value for the 95% confidence interval is 1.9 + 0.06, or <strong>1.96</strong>.
      </p>
      <p>
        When population variance is <strong>unknown</strong>, we need to use the t-statistic from the <nuxt-link to="/tables/students-t-distribution-table/" target="_blank">
          Student’s t table
        </nuxt-link>, which gives us the right tail area.
      </p>
      <img
        class="known-unknown" loading="lazy" alt="Unknown standard deviations graph."
        src="../../../../assets/images/calculators/confidence-interval/confidence-interval-05-desktop.png"
      >
      <p class="bottom-margins">
        One important characteristic of Student’s t-statistic is that it depends on the degrees of freedom. The latter is equal to the sample size (n) - 1. Put simply, for a sample of n, we have <strong>n-1</strong> degrees. Following this logic, a sample of 20 observations would have 19 degrees of freedom.
      </p>
      <p class="math-jax-display">
        The rows in the Student’s t table indicate different degrees of freedom, abbreviated as d.f., while the columns show common \[\alpha\] values. Let’s say that we want to find the t-value for the 95% confidence interval. This means that \[\alpha\] is equal to 5%. Therefore, \[\alpha/2\] would be 2.5% or <strong>0.025</strong> and the associated t-statistic equals <strong>2.09</strong>.
      </p>
      <p class="tables-headings">
        Student’s t-Distribution Table
      </p>
      <p class="tables-small-heading">
        Area in Upper Tail
      </p>
      <img
        class="img-tables std" loading="lazy" alt="Student\'s T-Distribution Table."
        src="../../../../assets/images/calculators/confidence-interval/confidence-interval-06-desktop.webp"
      >
      <p class="bottom-margins">
        Note that after 30 degrees of freedom, the t-statistic table becomes almost the same as the z-score. As the degrees depend on the sample we observe that, in essence, the bigger the sample, the closer we get to the actual numbers. A common rule of thumb for a sample containing more than 50 observations is to use the z-table instead of the t-table.
      </p>
      <p class="bottom-margins">
        In summary, the Confidence Interval Calculator involves determining three parameters: the sample mean value (μ), the population standard deviation (σ) when the variance is known, and the sample standard deviation (s) when the variance is unknown, as well as the sample size (n).
      </p>
    </div>

    <div id="heading5" class="article-section">
      <h3 class="h4">
        How to Calculate a Confidence Interval with Precision
      </h3>
      <p>The confidence intervals could be summarized as follows:</p>
      <p class="formulas-top-down confidence-interval">
        \[\left[\bar{X}-ME,\bar{X}+ME\right]\]
      </p>
      <p class="bottom-margins">
        The true population mean (µ) falls within the interval defined by the sample mean +/- the margin of error (МЕ).
      </p>
      <p class="bottom-margins">
        Since we want a better prediction, it is in our interest to have the narrowest confidence interval possible. If the range is too wide, we can’t be certain that the <strong>true population mean</strong> (µ) falls within the interval.
      </p>
      <p class="bottom-margins">
        Getting a smaller margin of error means that the confidence interval would be narrower. As it happens, we can actually control the ME. It consists of three parts:
      </p>
      <ul class="bottom-margins">
        <li>Z-score and t-statistic</li>
        <li>Standard deviation</li>
        <li>Sample size</li>
      </ul>
      <p class="bottom-margins">
        The statistic and the standard deviation are in the numerator, so smaller values for those will reduce the margin of error.
      </p>
      <p class="bottom-margins">
        All else equals a higher level of confidence, which results in a higher margin of error. This leads to a wider confidence interval. Therefore, if we keep the standard deviation and the sample size at a constant lower confidence level will result in a narrower interval. In other words, when our confidence is lower, the confidence interval itself is smaller
      </p>
      <p class="bottom-margins">
        What about the standard deviation? A lower value means that the dataset is more concentrated around the mean, so we have a better chance to get it right.
      </p>
      <p>Lastly, we have the sample size in the denominator. Higher sizes will decrease the margin of error as the more observations you have in your sample, the more certain you are in the prediction. We can conclude, therefore, that the more observations in the sample, the higher the chances of getting a good idea about the entire population’s true mean.</p>
      <img
        class="last-image" loading="lazy" alt="Variables effect on the confidence level width."
        src="../../../../assets/images/calculators/confidence-interval/confidence-interval-07-desktop.png"
      >
    </div>
  </div>
</template>

<script>
export default {
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.row {
  .articles-container {
    p {
      margin: 0;
    }

    .article-section {
      img {
        &.img-interval {
          margin-bottom: 0;
        }
      }

      .snd,
      .std {
        margin-top: var(--spacing-20);
      }

      .tables-headings {
        font-weight: 500;
        font-size: 24px;
        color: var(--accent-shade-2);
        text-align: center;
        margin-top: var(--spacing-40);
      }

      .tables-small-heading {
        font-size: 21px;
        color: var(--accent-shade-2);
        text-align: center;
        margin-top: var(--spacing-12);
      }

      .formulas-top-down {
        overflow: auto;
      }
    }
  }
}

@include bp($bp-laptop) {
  .row .articles-container .article-section {
    .snd,
    .std {
      margin-top: var(--spacing-16);
    }

    .tables-headings {
      font-size: 21px;
    }

    .tables-small-heading {
      font-size: 18px;
      margin-top: 10px;
    }
  }
}

@include bp($bp-mobile) {
  .row {
    .articles-container {
      .article-section {
        .snd,
        .std {
          margin-top: var(--spacing-8);
        }

        .tables-headings {
          font-size: 16px;
        }

        .tables-small-heading {
          font-size: 14px;
          margin-top: var(--spacing-6);
        }

        .t-unknown {
          margin-bottom: var(--spacing-16);
        }

        .last-image {
          margin-bottom: var(--spacing-20);
        }
      }
    }
  }
}

@import '../../../../assets/sass/calculators/articles.scss';
</style>
